<template>
  <div class="catalog-options">
    <p>
      {{ $t("GenerateCatalogFromExternalData.CatalogOptions.InfoText") }}
    </p>
    <p class="options-headline">{{ $t("GenerateCatalogFromExternalData.ExternalDataSource") }}</p>
    <MSelect v-model="vm.externalDataConnection" :items="formatedConnectionStrings" textField="name" valueField="id" />
    <div class="test-connection-wrapper my-3">
      <Alert v-if="vm.isLoading" key="loading" :show="true" class="connection-alert w-100 m-0">
        <span class="d-flex align-items-center gap-2 alert-text-align">
          <Spinner :size="20" />
          {{ $t("GenerateCatalogFromExternalData.testingConnectionMessage") }}
        </span>
      </Alert>

      <Alert
        v-else-if="!!vm.externalDataConnection && vm.connectionState !== null"
        key="status"
        :show="true"
        class="connection-alert w-100 m-0"
        with-icon
        :variant="vm.connectionState ? 'success' : 'danger'">
        {{ vm.connectionState ? $t("GenerateCatalogFromExternalData.ConnectionEstablished") : vm.errorMessage }}
      </Alert>
    </div>
    <div class="row">
      <div class="col">
        <div class="d-flex flex-column">
          <label class="font-weight-bold">{{ $t("Source") }}</label>
          <span>{{ vm.singleColumnName ? vm.singleColumnName[0] : "" }}</span>
        </div>
      </div>
      <div class="col dropdown-right">
        <label class="font-weight-bold">{{ $t("TargetField") }}</label>
        <dropdown v-model="vm.artNrType" :items="artNrTypeFields" value-field="value" text-field="text">
          <template #item-template="{ entry }">
            <span v-if="entry"> {{ entry.text }} </span>
          </template>
        </dropdown>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import MSelect from "@/BasicComponentLibrary/MSelect.vue";
import Dropdown from "@/common/components/Dropdown.vue";
import articleNumberTypesApi from "@/services/articleNumberTypes";
import Alert from "@common/components/Alert.vue";
import Spinner from "@common/components/Spinner.vue";
import externalDataApi from "@services/externalData";
import { computed, onMounted, reactive, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t: $t } = useI18n();

//#region Props & Emits
const props = defineProps<{
  fields: {
    artNrTypeSelected: number;
    artNrSourceSelected: number | null;
  } | null;
  groupedCommands: Array<{
    connectionName: string;
    selectionCommands: Array<{ id: string; name: string }>;
  }> | null;
  direction?: string;
}>();

const emits = defineEmits(["stepChange", "selectedExternalData"]);

//#endregion

//#region ViewModel
type ViewModel = {
  connectionState: boolean | null;
  externalDataConnection: string;
  artNrType: number;
  artNrSource: number | null;
  artNrTypesFetched: Array<{ name: string; articleNumberType: string }>;
  artNrSourcesFetched: string[];
  errorMessage: string;
  isLoading: boolean;
  selectedCommands: Array<{
    connectionName: string;
    selectionCommands: Array<{ id: string; name: string }>;
  }> | null;
  singleColumnName: string | null;
};

const vm = reactive<ViewModel>({
  connectionState: null,
  externalDataConnection: "",
  artNrType: props.fields ? props.fields.artNrTypeSelected : 0,
  artNrSource: props.fields ? props.fields.artNrSourceSelected : null,
  artNrTypesFetched: [],
  artNrSourcesFetched: [],
  errorMessage: "",
  isLoading: false,
  selectedCommands: null,
  singleColumnName: null
});

//#endregion

//#region Lifecycle
onMounted(() => {
  getAllSelectionCommandsGrouped();
  getArtNrTypes();
});
//#endregion

const artNrTypeFields = computed(() => {
  return vm.artNrTypesFetched.map((x, index) => ({
    text: x.name,
    value: index
  }));
});

const formatedConnectionStrings = computed(() => {
  if (!vm.selectedCommands || !Array.isArray(vm.selectedCommands)) return {};

  return vm.selectedCommands.reduce((acc: Record<string, Array<{ id: string; name: string }>>, item) => {
    acc[item.connectionName] = item.selectionCommands.map((cmd) => ({
      id: cmd.id,
      name: cmd.name
    }));
    return acc;
  }, {});
});

async function getAllSelectionCommandsGrouped() {
  try {
    vm.selectedCommands = await externalDataApi.getAllSelectionCommandsGrouped();
  } catch (err) {
    console.error("External data api failed:", err);
  }
}

const checkArticleNrType = (selectedArtNrType: string) => {
  let isDefault = false;
  vm.artNrTypesFetched.map((artNrType) => {
    if (artNrType.name === selectedArtNrType && artNrType.articleNumberType === "Default") isDefault = true;
  });
  return isDefault;
};

async function getArtNrTypes() {
  try {
    vm.artNrTypesFetched = await articleNumberTypesApi.get();
  } catch (err) {
    console.error("External data api failed:", err);
  }
}

watch(
  () => vm.artNrType,
  () => {
    emits("selectedExternalData", {
      singleColumnName: vm.singleColumnName,
      fields: {
        artNrTypeSelected: vm.artNrType,
        artNrSourceSelected: vm.artNrSource
      },
      connectionState: vm.connectionState
    });
  }
);

watch(
  () => vm.singleColumnName,
  () => {
    const artNrTypeName = vm.artNrTypesFetched[vm.artNrType]?.name;
    emits("selectedExternalData", {
      singleColumnName: vm.singleColumnName,
      commandId: vm.externalDataConnection,
      selectedArticleNumberType: {
        articleNumberType: checkArticleNrType(artNrTypeName) ? 0 : 1,
        name: artNrTypeName
      },
      connectionState: vm.connectionState
    });
  }
);

watch(
  () => vm.externalDataConnection,
  async () => {
    vm.singleColumnName = null;
    try {
      vm.isLoading = true;
      vm.singleColumnName = await externalDataApi.getSourceColumnNameOfSelectionCommand(vm.externalDataConnection);

      if (vm.singleColumnName && vm.singleColumnName.length === 1) {
        vm.connectionState = true;
      }
    } catch (err: any) {
      vm.errorMessage = err.status === 409 ? err.response.data : $t("GenerateCatalogFromExternalData.ConnectionFailed");
      vm.connectionState = false;
    } finally {
      vm.isLoading = false;
    }
  }
);
</script>
